import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  of,
  startWith,
} from 'rxjs';

import { ACTIVITY_SEGMENT } from '../../shared/constants/activity-segment';
import { BRAZIL_STATES } from '../../shared/constants/brazil-states.constant';
import { PartnerFormRequest } from '../../core/models/partner-form';
import { PartnersApplicationService } from '../../core/services/partners-application';
import { I18nService } from '../../core/services/i18n.service';
import { IBGECity } from '../../shared/interfaces/ibge-city.interface';
import { CustomValidators } from '../../shared/validators';
import { ToastService } from '../../shared/services/toast.service';
import { formatCnpj, unformatCnpj } from '../../core/utils/cnpj.util';

@Component({
  selector: 'umkt-formulario-parceiros',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './formulario-parceiros.component.html',
  styleUrl: './formulario-parceiros.component.scss',
})
export class FormularioParceiroComponent implements OnInit, OnDestroy {
  private static readonly BODY_CLASS = 'umkt-websummit-contato-page';
  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly partnersApplication = inject(PartnersApplicationService);
  private readonly i18n = inject(I18nService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);

  readonly currentYear = new Date().getFullYear();
  readonly states = BRAZIL_STATES;
  readonly languages = [
    { code: 'pt-BR', name: 'PT-BR', flag: 'assets/images/countries/brazil.svg', alt: 'Brasil' },
    { code: 'en-US', name: 'EN-US', flag: 'assets/images/countries/usa.svg', alt: 'Estados Unidos' },
    { code: 'es-ES', name: 'ES-ES', flag: 'assets/images/countries/spain.svg', alt: 'Espanha' },
  ];
  activitySegments: { value: string; label: string }[] = [];
  yesNoOptions: { label: string; value: boolean }[] = [];
  businessExperienceOptions: { label: string; value: string }[] = [];
  investmentRangeOptions: { label: string; value: string }[] = [];
  dedicationOptions: { label: string; value: string }[] = [];
  motivationOptions: { label: string; value: string }[] = [];
  title = '';
  brandAlt = '';
  nameLabel = '';
  phoneLabel = '';
  phone2Label = '';
  emailLabel = '';
  cnpjLabel = '';
  stateLabel = '';
  cityLabel = '';
  selectLabel = '';
  loadingCitiesLabel = '';
  sendingLabel = '';
  submitLabel = '';
  clearLabel = '';
  successMessage = '';
  copyrightText = '';
  rightsReservedText = '';
  businessOwnerQuestion = '';
  businessSegmentQuestion = '';
  businessExperienceQuestion = '';
  initialInvestmentQuestion = '';
  investmentRangeQuestion = '';
  salesExperienceQuestion = '';
  businessNetworkQuestion = '';
  dedicationQuestion = '';
  commercialStructureQuestion = '';
  motivationQuestion = '';
  successReasonQuestion = '';
  selectedLanguage = this.languages[0];
  isLanguageMenuOpen = false;

  submitted = false;
  showSuccess = false;
  isSubmitting = false;
  isLoadingCities = false;
  cities: string[] = [];
  private cityRequestId = 0;

  readonly form = this.fb.group({
    nome: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(120)]],
    telefone: ['', [Validators.required, CustomValidators.phone]],
    telefone2: ['', [CustomValidators.phone]],
    email: ['', [Validators.email]],
    cnpj: ['', [Validators.required, CustomValidators.cnpj]],
    estado: ['', [Validators.required]],
    cidade: this.fb.control({ value: '', disabled: true }, [Validators.required]),
    empresario: [null as boolean | null, [CustomValidators.requiredValue]],
    ramoAtividade: [null as string | null],
    experienciaEmpresarial: ['', [Validators.required]],
    investimentoInicial: [null as boolean | null, [CustomValidators.requiredValue]],
    faixaInvestimento: [null as string | null],
    experienciaVendas: [null as boolean | null, [CustomValidators.requiredValue]],
    relacionamentoEmpresarios: [null as boolean | null, [CustomValidators.requiredValue]],
    dedicacao: ['', [Validators.required]],
    estruturaComercial: [null as boolean | null, [CustomValidators.requiredValue]],
    motivacao: ['', [Validators.required]],
    justificativa: ['', [Validators.required, Validators.maxLength(2000)]],
  });

  ngOnInit(): void {
    if (typeof window !== 'undefined') {
      this.renderer.addClass(this.document.body, FormularioParceiroComponent.BODY_CLASS);
    }

    this.loadTranslations();

    this.i18n
      .getCurrentLanguage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const langCode = this.i18n.getCurrentLanguageSync();
        const foundLanguage = this.languages.find((lang) => lang.code === langCode);
        if (foundLanguage) {
          this.selectedLanguage = foundLanguage;
        }

        this.loadTranslations();
      });

    this.form.controls.estado.valueChanges
      .pipe(
        startWith(this.form.controls.estado.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((stateCode) => {
        this.onStateChanged(String(stateCode ?? ''));
      });

    this.form.controls.empresario.valueChanges
      .pipe(
        startWith(this.form.controls.empresario.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        this.syncBusinessSegmentControl(this.isYesChoice(value));
      });

    this.form.controls.investimentoInicial.valueChanges
      .pipe(
        startWith(this.form.controls.investimentoInicial.value),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        this.syncInvestmentRangeControl(this.isYesChoice(value));
      });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      this.renderer.removeClass(this.document.body, FormularioParceiroComponent.BODY_CLASS);
    }
  }

  get canSubmit(): boolean {
    return this.form.valid && !this.isSubmitting;
  }

  get canClear(): boolean {
    const v = this.form.getRawValue();
    return Object.values(v).some((value) => String(value ?? '').trim() !== '');
  }

  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = formatCnpj(input.value);
    this.form.controls.cnpj.setValue(masked, { emitEvent: false });
  }

  onPhoneInput(event: Event, controlName: 'telefone' | 'telefone2'): void {
    const digits = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 11);
    this.form.controls[controlName].setValue(this.maskPhone(digits), { emitEvent: false });
  }

  toggleLanguageMenu(): void {
    this.isLanguageMenuOpen = !this.isLanguageMenuOpen;
  }

  selectLanguage(languageCode: string): void {
    if (this.selectedLanguage.code === languageCode) {
      this.isLanguageMenuOpen = false;
      return;
    }

    this.i18n.setLanguage(languageCode).subscribe({
      next: () => {
        const foundLanguage = this.languages.find((lang) => lang.code === languageCode);
        if (foundLanguage) {
          this.selectedLanguage = foundLanguage;
        }

        this.isLanguageMenuOpen = false;
      },
      error: () => {
        this.isLanguageMenuOpen = false;
      },
    });
  }

  showError(controlName: keyof typeof this.form.controls): boolean {
    const control = this.form.get(controlName);
    return !!(control && control.invalid && (control.touched || this.submitted));
  }

  errorMessage(controlName: keyof typeof this.form.controls): string {
    const control = this.form.get(controlName);
    if (!control?.errors) return '';

    if (control.errors['required'] || control.errors['requiredValue']) {
      return this.i18n.instant('shared.messages.required');
    }

    if (control.errors['minlength']) {
      return this.i18n.instant('pages.partnerForm.errors.minLengthName');
    }

    if (control.errors['maxlength']) {
      return this.i18n.instant('pages.partnerForm.errors.maxLengthJustification');
    }

    if (control.errors['invalidCNPJ']) {
      return this.i18n.instant('pages.partnerForm.errors.invalidCnpj');
    }

    if (control.errors['invalidPhone']) {
      return this.i18n.instant('pages.partnerForm.errors.invalidPhone');
    }

    if (control.errors['email']) {
      return this.i18n.instant('pages.partnerForm.errors.invalidEmail');
    }

    return this.i18n.instant('shared.messages.invalid');
  }

  onSubmit(): void {
    this.submitted = true;
    this.showSuccess = false;

    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;

    this.partnersApplication
      .submitPartnerForm(this.buildRequestPayload())
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.showSuccess = true;
          this.resetForm();
          this.submitted = false;
        },
        error: (error) => {
          const message =
            error?.error?.msg ??
            error?.error?.message ??
            'Não foi possível enviar seus dados. Tente novamente em instantes.';
          this.toastService.showError(message, 5000, 'top-center');
        },
      });
  }

  private buildRequestPayload(): PartnerFormRequest {
    const raw = this.form.getRawValue();
    return {
      name: String(raw.nome ?? '').trim(),
      phone: this.buildPhoneList(raw.telefone, raw.telefone2),
      email: this.buildOptionalString(raw.email),
      cnpj: unformatCnpj(raw.cnpj ?? ''),
      state: String(raw.estado ?? ''),
      city: String(raw.cidade ?? ''),
      isBusinessOwner: this.toBooleanChoice(raw.empresario) ?? false,
      currentBusinessSector: this.isYesChoice(raw.empresario) ? raw.ramoAtividade ?? null : null,
      yearsOfBusinessExperience: String(raw.experienciaEmpresarial ?? ''),
      hasInitialInvestment: this.toBooleanChoice(raw.investimentoInicial) ?? false,
      investmentRange: this.isYesChoice(raw.investimentoInicial) ? raw.faixaInvestimento ?? null : null,
      hasSalesOrCommercialManagementExperience: this.toBooleanChoice(raw.experienciaVendas) ?? false,
      hasBusinessNetwork: this.toBooleanChoice(raw.relacionamentoEmpresarios) ?? false,
      businessDedication: String(raw.dedicacao ?? ''),
      hasCommercialStructure: this.toBooleanChoice(raw.estruturaComercial) ?? false,
      motivation: String(raw.motivacao ?? ''),
      successReason: String(raw.justificativa ?? '').trim(),
    };
  }

  /**
   * Monta o array de telefones enviado ao backend.
   * O primeiro campo (telefone) é obrigatório; o segundo (telefone2) é opcional.
   * Campos vazios são descartados, então o array pode ter 1 ou 2 posições.
   */
  private buildPhoneList(primary: string | null | undefined, secondary: string | null | undefined): string[] {
    return [primary, secondary]
      .map((phone) => this.onlyDigits(phone ?? ''))
      .filter((phone) => phone.length > 0);
  }

  private buildOptionalString(value: string | null | undefined): string | null {
    const trimmed = String(value ?? '').trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private onStateChanged(stateCode: string): void {
    const cityControl = this.form.controls.cidade;
    cityControl.reset('', { emitEvent: false });
    this.cities = [];
    this.cityRequestId += 1;

    if (!stateCode) {
      cityControl.disable({ emitEvent: false });
      this.isLoadingCities = false;
      return;
    }

    cityControl.enable({ emitEvent: false });
    this.loadCitiesFromIBGE(stateCode);
  }

  private loadCitiesFromIBGE(stateCode: string): void {
    const requestId = this.cityRequestId;
    this.isLoadingCities = true;

    this.http
      .get<IBGECity[]>(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${stateCode}/municipios`)
      .pipe(
        catchError(() => {
          if (requestId === this.cityRequestId) {
            this.cities = [];
            this.toastService.showError('Não foi possível carregar os municípios.', 5000, 'top-center');
          }

          return of([] as IBGECity[]);
        }),
        finalize(() => {
          if (requestId === this.cityRequestId) {
            this.isLoadingCities = false;
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        if (requestId !== this.cityRequestId) {
          return;
        }

        this.cities = (data ?? [])
          .map((city) => city.nome)
          .sort((left, right) => left.localeCompare(right, 'pt-BR'));
      });
  }

  private syncBusinessSegmentControl(showField: boolean): void {
    const control = this.form.controls.ramoAtividade;

    if (showField) {
      control.setValidators([Validators.required]);
      control.markAsTouched();
    } else {
      control.clearValidators();
      control.reset(null, { emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private syncInvestmentRangeControl(showField: boolean): void {
    const control = this.form.controls.faixaInvestimento;

    if (showField) {
      control.setValidators([Validators.required]);
      control.markAsTouched();
    } else {
      control.clearValidators();
      control.reset(null, { emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private resetForm(): void {
    this.form.reset(
      {
        nome: '',
        telefone: '',
        telefone2: '',
        email: '',
        cnpj: '',
        estado: '',
        cidade: { value: '', disabled: true },
        empresario: null,
        ramoAtividade: null,
        experienciaEmpresarial: '',
        investimentoInicial: null,
        faixaInvestimento: null,
        experienciaVendas: null,
        relacionamentoEmpresarios: null,
        dedicacao: '',
        estruturaComercial: null,
        motivacao: '',
        justificativa: '',
      },
      { emitEvent: true }
    );

    this.cities = [];
    this.isLoadingCities = false;
    this.cityRequestId += 1;
    this.syncBusinessSegmentControl(false);
    this.syncInvestmentRangeControl(false);
    this.form.controls.cidade.disable({ emitEvent: false });
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private maskPhone(digits: string): string {
    if (!digits) {
      return '';
    }

    if (digits.length <= 2) {
      return `(${digits}`;
    }

    const areaCode = digits.slice(0, 2);
    const subscriber = digits.slice(2);

    if (subscriber.length <= 4) {
      return `(${areaCode}) ${subscriber}`;
    }

    const firstPart = subscriber.slice(0, subscriber.length - 4);
    const secondPart = subscriber.slice(-4);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  }

  isYesChoice(value: boolean | string | null | undefined): boolean {
    return value === true || value === 'true';
  }

  private toBooleanChoice(value: boolean | string | null | undefined): boolean | null {
    if (value === true || value === 'true') {
      return true;
    }

    if (value === false || value === 'false') {
      return false;
    }

    return null;
  }

  private onlyDigits(value: string): string {
    return value.replace(/\D/g, '');
  }

  private loadTranslations(): void {
    this.brandAlt = this.i18n.instant('pages.partnerForm.brandAlt');
    this.title = this.i18n.instant('pages.partnerForm.title');
    this.nameLabel = this.i18n.instant('shared.labels.name');
    this.phoneLabel = this.i18n.instant('shared.labels.phone');
    this.phone2Label = this.i18n.instant('shared.labels.phone2');
    this.emailLabel = this.i18n.instant('shared.labels.email');
    this.cnpjLabel = this.i18n.instant('shared.labels.cnpj');
    this.stateLabel = this.i18n.instant('shared.labels.state');
    this.cityLabel = this.i18n.instant('shared.labels.city');
    this.selectLabel = this.i18n.instant('shared.placeholders.select');
    this.loadingCitiesLabel = this.i18n.instant('pages.partnerForm.messages.loadingCities');
    this.sendingLabel = this.i18n.instant('pages.partnerForm.messages.sending');
    this.submitLabel = this.i18n.instant('shared.buttons.submit');
    this.clearLabel = this.i18n.instant('shared.buttons.clear');
    this.successMessage = this.i18n.instant('pages.partnerForm.messages.success');
    this.copyrightText = this.i18n.instant('pages.partnerForm.messages.copyright', { year: this.currentYear });
    this.rightsReservedText = this.i18n.instant('components.footer.rightsReserved');

    this.businessOwnerQuestion = this.i18n.instant('pages.partnerForm.questions.businessOwner');
    this.businessSegmentQuestion = this.i18n.instant('pages.partnerForm.questions.currentBusinessSector');
    this.businessExperienceQuestion = this.i18n.instant('pages.partnerForm.questions.yearsOfBusinessExperience');
    this.initialInvestmentQuestion = this.i18n.instant('pages.partnerForm.questions.initialInvestment');
    this.investmentRangeQuestion = this.i18n.instant('pages.partnerForm.questions.investmentRange');
    this.salesExperienceQuestion = this.i18n.instant('pages.partnerForm.questions.salesExperience');
    this.businessNetworkQuestion = this.i18n.instant('pages.partnerForm.questions.businessNetwork');
    this.dedicationQuestion = this.i18n.instant('pages.partnerForm.questions.businessDedication');
    this.commercialStructureQuestion = this.i18n.instant('pages.partnerForm.questions.commercialStructure');
    this.motivationQuestion = this.i18n.instant('pages.partnerForm.questions.motivation');
    this.successReasonQuestion = this.i18n.instant('pages.partnerForm.questions.successReason');

    this.yesNoOptions = [
      { label: this.i18n.instant('shared.labels.yes'), value: true },
      { label: this.i18n.instant('shared.labels.no'), value: false },
    ];
    this.businessExperienceOptions = [
      { label: this.i18n.instant('pages.partnerForm.options.yearsOfBusinessExperience.lessThan1'), value: 'LESS_THAN_1' },
      { label: this.i18n.instant('pages.partnerForm.options.yearsOfBusinessExperience.oneToThree'), value: 'FROM_1_TO_3' },
      { label: this.i18n.instant('pages.partnerForm.options.yearsOfBusinessExperience.threeToFive'), value: 'FROM_3_TO_5' },
      { label: this.i18n.instant('pages.partnerForm.options.yearsOfBusinessExperience.moreThanFive'), value: 'MORE_THAN_5' },
    ];
    this.investmentRangeOptions = [
      { label: this.i18n.instant('pages.partnerForm.options.investmentRange.upTo20k'), value: 'UP_TO_20K' },
      { label: this.i18n.instant('pages.partnerForm.options.investmentRange.twentyToFifty'), value: 'FROM_20K_TO_50k' },
      { label: this.i18n.instant('pages.partnerForm.options.investmentRange.fiftyToHundred'), value: 'FROM_50K_TO_100k' },
    ];
    this.dedicationOptions = [
      { label: this.i18n.instant('pages.partnerForm.options.businessDedication.fullTime'), value: 'FULL_TIME' },
      { label: this.i18n.instant('pages.partnerForm.options.businessDedication.partTime'), value: 'PART_TIME' },
      { label: this.i18n.instant('pages.partnerForm.options.businessDedication.investorOnly'), value: 'INVESTORS_ONLY' },
    ];
    this.motivationOptions = [
      { label: this.i18n.instant('pages.partnerForm.options.motivation.newIncome'), value: 'NEW_INCOME' },
      { label: this.i18n.instant('pages.partnerForm.options.motivation.businessExpansion'), value: 'BUSINESS_EXPANSION' },
      { label: this.i18n.instant('pages.partnerForm.options.motivation.careerChange'), value: 'CAREER_CHANGE' },
      { label: this.i18n.instant('pages.partnerForm.options.motivation.investment'), value: 'INVESTMENT' },
    ];
    this.activitySegments = ACTIVITY_SEGMENT.map((segment) => ({
      value: segment.value,
      label: this.i18n.instant(segment.labelKey),
    }));
  }

  onClear(): void {
    this.resetForm();
    this.submitted = false;
    this.showSuccess = false;
  }
}
